import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Modal } from "antd";
import Slider from "react-slick";
import {ArrowBackIos, ArrowForwardIos} from '@mui/icons-material';
import { Button } from '@mui/material';
import { handleListPopupWhatsNew } from "store/whatsNew/thunks";
import { useDispatch } from "react-redux";
import { useWhatsNew } from "store/whatsNew/selectors";
import { useCurrentUser } from "store/users/selectors";
import Loading from "components/Loading";

export default function TeacherWhatsNewModal({ open, onClose, onDontShow = () => null, dontLoader , from = "" }) {
  const dispatch = useDispatch();
  const currentUser = useCurrentUser();
  const whatsNewData = useWhatsNew();
  const [whatsNew, setWhatsNew] = useState([]);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    if(from === "welcome_video"){
      return;
    }
    const data = {
      user_id: currentUser?.id
    }
    dispatch(handleListPopupWhatsNew(data));
  }, [from]);

  useEffect(() => {
    if (whatsNewData) {
      const filterByRole = whatsNewData?.filter((k) =>
        k?.user_role.includes(currentUser?.role)
      );
      setWhatsNew(filterByRole);
    }
  }, [whatsNewData]);

  useEffect(() => {
    if (whatsNew?.length) {
      setSelectedId(whatsNew[0].id);
    }
  }, [whatsNew]);

  const selectedItem = whatsNew?.find((item) => item.id === selectedId);

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 2,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    arrows: true,
    swipe: true,
    responsive: [
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
  };

  const handlePrev = () => {
    if (!whatsNew || whatsNew.length === 0) return;

    const currentIndex = whatsNew.findIndex((item) => item.id === selectedId);
    const prevIndex =
      currentIndex === 0 ? whatsNew.length - 1 : currentIndex - 1;
    setSelectedId(whatsNew[prevIndex].id);
  };

  const handleNext = () => {
    if (!whatsNew || whatsNew.length === 0) return;

    const currentIndex = whatsNew.findIndex((item) => item.id === selectedId);
    const nextIndex = (currentIndex + 1) % whatsNew.length;
    setSelectedId(whatsNew[nextIndex].id);
  };

  if (!selectedItem?.id) return null;

  return (
    <Modal
      className="techrWhatsNewModal"
      open={open}
      onCancel={onClose}
      footer={null}
      width={870}
      centered
      bodyStyle={{
        padding: 0,
        backgroundColor: "#0a0a0a",
        maxHeight: "85vh",
        overflowY: "auto",
      }}
    >
      <Wrapper>
        <Header>
          <Title>🚀 What’s New</Title>
          {whatsNew?.length > 1 && (
            <ArrowControls>
              <ArrowBtn type="button" onClick={handlePrev}>
                <ArrowBackIos />
              </ArrowBtn>
              <ArrowBtn type="button" onClick={handleNext}>
                <ArrowForwardIos />
              </ArrowBtn>
            </ArrowControls>
          )}
        </Header>

        <ContentSection>
          <DateWrapper>
            <DateLine />
            <StyledDate>
              {new Date(selectedItem.create_date).toLocaleDateString("en-GB", {
                day: "numeric",
                month: "short",
                year: "numeric",
              })}
            </StyledDate>
            <DateLine />
          </DateWrapper>

          <Intro>
            <Highlight>✨ Introducing</Highlight>{" "}
            <span style={{ color: "white" }}>{selectedItem.title}</span>
          </Intro>

          <MediaContainer>
            <StyledVideo
              autoPlay
              loop
              muted
              controls
              src={selectedItem.video_url}
            />
          </MediaContainer>
          <Description
            className="techrDetailSection"
            dangerouslySetInnerHTML={{ __html: selectedItem.long_description }}
          />
          {selectedItem.image_urls.length > 1 ? (
            <ImageSlider {...sliderSettings}>
              {selectedItem.image_urls.map((image, index) => (
                <StyledImage
                  key={index}
                  src={image}
                  alt={`Slide ${index + 1}`}
                />
              ))}
            </ImageSlider>
          ) : (
            <SingleImage src={selectedItem.image_urls[0]} alt="Preview" />
          )}

          <Description
            className="techrDetailSection"
            dangerouslySetInnerHTML={{ __html: selectedItem.short_description }}
          />
        </ContentSection>

        {/* Buttons at the end */}
        <ButtonWrapper className="teacherWhatsNewBtn">
          <Button
            variant="contained"
            size="large"
            color="primary"
            onClick={onClose}
          >
            WATCH LATER
          </Button>
          <Button
            variant="contained"
            size="large"
            color="secondary"
            onClick={() => {
              onDontShow(selectedItem);
            }}
          >
            {dontLoader && <Loading size={18} />} DON'T SHOW AGAIN
          </Button>
        </ButtonWrapper>
      </Wrapper>
    </Modal>
  );
}

// Styled Components
const Wrapper = styled.div`
  background-color: #0a0a0a;
  color: #fff;
  margin: 0 auto;
  text-align: center;
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const Header = styled.div`
  background-color: #0a0e16;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  padding: 1rem;
  border-bottom: 1px solid #222;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  color: white;
`;

const ArrowControls = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-right: 30px;
`;

const ArrowBtn = styled.button.attrs(() => ({ type: "button" }))`
  background: linear-gradient(90deg, #cb5b9c, #a852e5, #7466d1);
  border: none;
  color: #fff;
  font-size: 1.2rem;
  cursor: pointer;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.3s;

  &:hover {
    background: linear-gradient(90deg, #8a3b6d, #6c2ca8, #4a3ba0);
    color: #fff;
  }
`;

const ContentSection = styled.div`
  padding: 2rem 1rem;
  flex-grow: 1;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 20px;
  margin-top: auto;
  padding: 1rem;
  position: absolute;
  bottom: -64px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;

  @media screen and (max-width: 400px) {
    gap: 10px;
  }
`;

const StyledDate = styled.div`
  font-size: 0.9rem;
  color: #bbb;
`;

const DateWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 20px 0;
`;

const DateLine = styled.div`
  flex: 1;
  height: 1px;
  background-color: #222;
  margin: 0 10px;
`;

const Intro = styled.h2`
  font-size: 1.4rem;
  margin: 1rem 0 0.5rem;
`;

const Highlight = styled.span`
  background: linear-gradient(90deg, #cb5b9c, #a852e5, #7466d1);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const MediaContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 2rem 0;
`;

const StyledVideo = styled.video`
  width: 100%;
  max-width: 640px;
  border-radius: 8px;
`;

const StyledImage = styled.img`
  width: 100%;
  max-height: 300px;
  object-fit: contain;
  border-radius: 8px;
`;

const SingleImage = styled.img`
  width: 100%;
  max-width: 640px;
  border-radius: 8px;
  margin: 2rem 0;
`;

// const ImageSlider = styled(Slider)`
//   max-width: 800px;
//   margin: 2rem auto;

//   .slick-slide > div {
//     padding: 0 10px;
//   }

//   .slick-list {
//     margin: 0 -10px;
//   }

//   .slick-dots li button:before,
//   .slick-prev:before,
//   .slick-next:before {
//     color: #fff;
//   }
// `;

const ImageSlider = styled(Slider)`
  max-width: 800px;
  margin: 2rem auto;

  .slick-slide > div {
    padding: 0 10px; /* Controls space between images */
  }

  .slick-list {
    margin: 0 -10px; /* Compensates for padding on the edges */
  }

  .slick-slide img {
    width: 100%;
    height: auto;
    border-radius: 8px;
  }

  .slick-dots li button:before {
    color: #fff;
  }

  .slick-prev:before,
  .slick-next:before {
    color: #fff;
  }
`;


const Description = styled.p`
  color: #ccc;
  text-align: left;
`;
